import { z } from "zod"

import {
  branchIdSchema,
  entityIdSchema,
  legalEntityIdSchema,
} from "./identifiers.js"

const dateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)
const currencySchema = z.string().regex(/^[A-Z]{3}$/)
const reasonSchema = z.string().trim().min(12).max(500)

const stepSchema = z.object({
  stepCode: z.string().regex(/^[a-z][a-z0-9_]{2,39}$/),
  minimumAgeDays: z.number().int().min(1).max(365),
  kind: z.enum(["reminder", "final_notice", "future_work_hold"]),
  channel: z.enum(["email", "sms", "in_app"]),
  messageCode: z.enum([
    "invoice_overdue",
    "invoice_final_notice",
    "future_work_on_hold",
  ]),
}).strict()

export const collectionsPolicyPublishV1Schema = z.object({
  policyId: entityIdSchema,
  policyCode: z.string().regex(/^[A-Z][A-Z0-9_]{2,39}$/),
  policyVersion: z.number().int().min(1).max(10_000),
  legalEntityId: legalEntityIdSchema,
  effectiveFrom: dateSchema,
  effectiveUntil: dateSchema.nullable(),
  promiseGraceDays: z.number().int().min(1).max(90),
  steps: z.array(stepSchema).min(1).max(10),
  publicationReason: reasonSchema,
}).strict().superRefine((value, context) => {
  if (
    value.effectiveUntil !== null &&
    value.effectiveUntil < value.effectiveFrom
  ) context.addIssue({ code: "custom", message: "Invalid effective window." })
  const ordered = [...value.steps].sort(
    (left, right) => left.minimumAgeDays - right.minimumAgeDays,
  )
  if (
    new Set(value.steps.map((step) => step.stepCode)).size !== value.steps.length ||
    ordered.some((step, index) =>
      index > 0 && step.minimumAgeDays === ordered[index - 1]?.minimumAgeDays
    )
  ) context.addIssue({ code: "custom", message: "Duplicate collection step." })
})

export const collectionsEvaluateV1Schema = z.object({
  actionId: entityIdSchema,
  invoiceId: entityIdSchema,
  policyId: entityIdSchema,
  policyVersion: z.number().int().min(1).max(10_000),
  legalEntityId: legalEntityIdSchema,
  branchId: branchIdSchema,
  contactId: entityIdSchema,
  observedDate: dateSchema,
  expectedStepCode: z.string().regex(/^[a-z][a-z0-9_]{2,39}$/),
  holdId: entityIdSchema.nullable(),
}).strict()

export const collectionsPromiseCreateV1Schema = z.object({
  promiseId: entityIdSchema,
  invoiceId: entityIdSchema,
  policyId: entityIdSchema,
  policyVersion: z.number().int().min(1).max(10_000),
  legalEntityId: legalEntityIdSchema,
  branchId: branchIdSchema,
  amountMinor: z.number().int().safe().positive(),
  currency: currencySchema,
  promisedFor: dateSchema,
  reason: reasonSchema,
}).strict()

export const collectionsPromiseTransitionV1Schema = z.object({
  promiseId: entityIdSchema,
  transition: z.enum(["kept", "broken", "canceled", "reopened"]),
  observedDate: dateSchema,
  newPromisedFor: dateSchema.nullable(),
  reason: reasonSchema,
}).strict().superRefine((value, context) => {
  if ((value.transition === "reopened") !== (value.newPromisedFor !== null)) {
    context.addIssue({ code: "custom", message: "Reopen date mismatch." })
  }
})

export const collectionsResultV1Schema = z.object({
  entityType: z.enum(["collections_policy", "collections_action", "collections_promise"]),
  entityId: entityIdSchema,
  state: z.string().min(1).max(40),
  version: z.number().int().positive(),
  legalEntityId: legalEntityIdSchema,
  invoiceId: entityIdSchema.nullable(),
  policyId: entityIdSchema,
  policyVersion: z.number().int().positive(),
  actionKind: z.enum([
    "policy_published",
    "reminder",
    "final_notice",
    "future_work_hold",
    "promise",
  ]),
  communicationState: z.enum(["not_applicable", "requested", "suppressed"]),
  completedWorkProtected: z.literal(true),
  holdId: entityIdSchema.nullable(),
}).strict()

export type CollectionsPolicyPublishV1 = z.infer<
  typeof collectionsPolicyPublishV1Schema
>
export type CollectionsEvaluateV1 = z.infer<typeof collectionsEvaluateV1Schema>
export type CollectionsPromiseCreateV1 = z.infer<
  typeof collectionsPromiseCreateV1Schema
>
export type CollectionsPromiseTransitionV1 = z.infer<
  typeof collectionsPromiseTransitionV1Schema
>
export type CollectionsResultV1 = z.infer<typeof collectionsResultV1Schema>
